import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Avatar,
  Dropdown,
  Modal,
  Form,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  Badge,
  Descriptions,
} from "antd";
import {
  UserOutlined,
  SearchOutlined,
  PlusOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserAddOutlined,
  TeamOutlined,
  CrownOutlined,
  CarOutlined,
  InfoCircleOutlined,
  MailOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { ColumnsType } from "antd/es/table";
import {
  userService,
  User,
  CreateUserData,
  UpdateUserData,
  UserFilters,
} from "../../services/userService";
import apiClient from "@/services/api";

const { Title, Text } = Typography;
const { Option } = Select;

interface Tenant {
  id: string;
  name: string;
  status: "active" | "suspended" | "inactive";
}

const SuperAdminUsers: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1); // Reset to first page when searching
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [roleFilter, statusFilter]);

  // Construire les filtres pour l'API
  const filters: UserFilters = {
    page: currentPage,
    limit: pageSize,
    ...(debouncedSearchText && { search: debouncedSearchText }),
    ...(roleFilter && { role: roleFilter as any }),
    ...(statusFilter === "active" && { is_active: true }),
    ...(statusFilter === "inactive" && { is_active: false }),
  };

  // Query to fetch tenants
  const { data: tenantsData, isLoading: tenantsLoading } = useQuery({
    queryKey: ["tenants"],
    queryFn: () =>
      apiClient.get<{ tenants: Tenant[] }>(
        "/super-admin/tenants?limit=100&status=active"
      ),
    select: (response) => response.tenants,
  });

  // Récupération des utilisateurs avec React Query
  const {
    data: usersData,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery({
    queryKey: ["users", filters],
    queryFn: () => userService.getUsers(filters),
    staleTime: 30000, // 30 secondes
  });

  // Récupération des statistiques
  const { data: stats } = useQuery({
    queryKey: ["user-stats"],
    queryFn: () => userService.getUserStats(),
    staleTime: 60000, // 1 minute
  });

  const users = usersData?.data || [];
  const pagination = usersData?.pagination;

  // Mutations pour les actions CRUD
  const createUserMutation = useMutation({
    mutationFn: (data: CreateUserData) => userService.createUser(data),
    onSuccess: () => {
      message.success("Utilisateur créé avec succès");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
      setIsModalVisible(false);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(
        error.response?.data?.error || "Erreur lors de la création"
      );
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserData }) =>
      userService.updateUser(id, data),
    onSuccess: () => {
      message.success("Utilisateur mis à jour avec succès");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
      setIsModalVisible(false);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(
        error.response?.data?.error || "Erreur lors de la mise à jour"
      );
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      message.success("Utilisateur supprimé avec succès");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
    },
    onError: (error: any) => {
      message.error(
        error.response?.data?.error || "Erreur lors de la suppression"
      );
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      userService.toggleUserStatus(id, isActive),
    onSuccess: () => {
      message.success("Statut utilisateur mis à jour");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
    },
    onError: (error: any) => {
      message.error(
        error.response?.data?.error ||
          "Erreur lors de la mise à jour du statut"
      );
    },
  });

  // Utilitaires
  const getRoleIcon = (role: string) => {
    switch (role) {
      case "super_admin":
        return <CrownOutlined style={{ color: "#faad14" }} />;
      case "admin":
        return <TeamOutlined style={{ color: "#1890ff" }} />;
      case "driver":
        return <CarOutlined style={{ color: "#52c41a" }} />;
      default:
        return <UserOutlined />;
    }
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case "super_admin":
        return "Super Admin";
      case "admin":
        return "Administrateur";
      case "driver":
        return "Chauffeur";
      default:
        return "Utilisateur";
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "super_admin":
        return "gold";
      case "admin":
        return "blue";
      case "driver":
        return "green";
      default:
        return "default";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Jamais";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Gestionnaires d'événements
  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({
      ...user,
      tenant_id: user.tenant_id || undefined,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (user: User) => {
    Modal.confirm({
      title: "Supprimer l'utilisateur",
      content: `Êtes-vous sûr de vouloir supprimer ${user.first_name} ${user.last_name} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => deleteUserMutation.mutate(user.id),
    });
  };

  const handleToggleStatus = (user: User) => {
    const action = user.is_active ? "désactiver" : "activer";
    Modal.confirm({
      title: `${
        action.charAt(0).toUpperCase() + action.slice(1)
      } l'utilisateur`,
      content: `Êtes-vous sûr de vouloir ${action} ${user.first_name} ${user.last_name} ?`,
      okText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: "Annuler",
      onOk: () =>
        toggleStatusMutation.mutate({ id: user.id, isActive: !user.is_active }),
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingUser) {
        // Mise à jour
        const { password, ...updateData } = values;
        updateUserMutation.mutate({
          id: editingUser.id,
          data: updateData,
        });
      } else {
        // Création
        createUserMutation.mutate(values);
      }
    } catch (error) {
      // L'erreur est gérée par les mutations
    }
  };

  const handleViewDetails = (user: User) => {
    setViewingUser(user);
    setIsDetailModalVisible(true);
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) setPageSize(size);
  };

  const getActionItems = (user: User) => [
    {
      key: "view",
      icon: <EyeOutlined />,
      label: "Voir les détails",
      onClick: () => handleViewDetails(user),
    },
    {
      key: "edit",
      icon: <EditOutlined />,
      label: "Modifier",
      onClick: () => handleEdit(user),
    },
    {
      key: "toggle",
      icon: <UserOutlined />,
      label: user.is_active ? "Désactiver" : "Activer",
      onClick: () => handleToggleStatus(user),
    },
    ...(user.role !== "super_admin"
      ? [
          {
            key: "delete",
            icon: <DeleteOutlined />,
            label: "Supprimer",
            danger: true,
            onClick: () => handleDelete(user),
          },
        ]
      : []),
  ];

  // Gestion du loading et des erreurs
  if (usersError) {
    return (
      <div style={{ padding: "24px" }}>
        <Card>
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Title level={4} type="danger">
              Erreur lors du chargement
            </Title>
            <Text type="secondary">
              Impossible de charger les utilisateurs. Veuillez réessayer.
            </Text>
            <br />
            <Button
              type="primary"
              style={{ marginTop: "16px" }}
              onClick={() => queryClient.invalidateQueries({ queryKey: ["users"] })}
            >
              Réessayer
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Colonnes du tableau
  const columns: ColumnsType<User> = [
    {
      title: "Utilisateur",
      key: "user",
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: "bold" }}>
              {record.first_name} {record.last_name}
            </div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.email}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Rôle",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag color={getRoleColor(role)} icon={getRoleIcon(role)}>
          {getRoleText(role)}
        </Tag>
      ),
      filters: [
        { text: "Super Admin", value: "super_admin" },
        { text: "Administrateur", value: "admin" },
        { text: "Chauffeur", value: "driver" },
      ],
    },
    {
      title: "Entreprise",
      dataIndex: "tenant_name",
      key: "tenant_name",
      render: (tenantName) => tenantName || <Text type="secondary">-</Text>,
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive) => (
        <Badge
          status={isActive ? "success" : "error"}
          text={isActive ? "Actif" : "Inactif"}
        />
      ),
      filters: [
        { text: "Actif", value: true },
        { text: "Inactif", value: false },
      ],
    },
    {
      title: "Dernière connexion",
      dataIndex: "last_login_at",
      key: "last_login_at",
      render: (lastLogin) => (
        <Text type="secondary">{formatDate(lastLogin)}</Text>
      ),
      sorter: (a, b) => {
        if (!a.last_login_at) return 1;
        if (!b.last_login_at) return -1;
        return (
          new Date(a.last_login_at).getTime() -
          new Date(b.last_login_at).getTime()
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Dropdown menu={{ items: getActionItems(record) }} trigger={["click"]}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: "24px" }}>
        <Title level={2}>Gestion des Utilisateurs</Title>
        <Text type="secondary">
          Gérez tous les utilisateurs de la plateforme FleetBase
        </Text>
      </div>

      {/* Statistiques */}
      <Row gutter={16} style={{ marginBottom: "24px" }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Utilisateurs"
              value={stats?.total_users || 0}
              prefix={<UserAddOutlined />}
              valueStyle={{ color: "#1890ff" }}
              loading={!stats}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Super Admins"
              value={stats?.super_admins || 0}
              prefix={<CrownOutlined />}
              valueStyle={{ color: "#faad14" }}
              loading={!stats}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Administrateurs"
              value={stats?.admins || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: "#52c41a" }}
              loading={!stats}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Chauffeurs"
              value={stats?.drivers || 0}
              prefix={<CarOutlined />}
              valueStyle={{ color: "#722ed1" }}
              loading={!stats}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtres et actions */}
      <Card style={{ marginBottom: "16px" }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space size="middle">
              <Input
                placeholder="Rechercher par nom, email ou entreprise..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 300 }}
              />
              <Select
                placeholder="Filtrer par rôle"
                value={roleFilter}
                onChange={setRoleFilter}
                style={{ width: 150 }}
                allowClear
              >
                <Option value="super_admin">Super Admin</Option>
                <Option value="admin">Administrateur</Option>
                <Option value="driver">Chauffeur</Option>
              </Select>
              <Select
                placeholder="Statut"
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 120 }}
                allowClear
              >
                <Option value="active">Actif</Option>
                <Option value="inactive">Inactif</Option>
              </Select>
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nouvel utilisateur
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tableau des utilisateurs */}
      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={usersLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} utilisateurs`,
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
          }}
        />
      </Card>

      {/* Modal de création/édition */}
      <Modal
        title={editingUser ? "Modifier l'utilisateur" : "Nouvel utilisateur"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Prénom"
                name="first_name"
                rules={[{ required: true, message: "Prénom requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Nom"
                name="last_name"
                rules={[{ required: true, message: "Nom requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Email requis" },
              { type: "email", message: "Email invalide" },
            ]}
          >
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Rôle"
                name="role"
                rules={[{ required: true, message: "Rôle requis" }]}
              >
                <Select>
                  <Option value="super_admin">Super Administrateur</Option>
                  <Option value="admin">Administrateur</Option>
                  <Option value="driver">Chauffeur</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Entreprise"
                name="tenant_id"
                tooltip="Laissez vide pour les super administrateurs"
              >
                <Select
                  placeholder="Sélectionner une entreprise"
                  allowClear
                  loading={tenantsLoading}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const children = option?.children?.toString() || "";
                    return children.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {tenantsData?.map((tenant: any) => (
                    <Option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {!editingUser && (
            <Form.Item
              label="Mot de passe"
              name="password"
              rules={[
                { required: true, message: "Mot de passe requis" },
                { min: 8, message: "Au moins 8 caractères" },
              ]}
            >
              <Input.Password />
            </Form.Item>
          )}

          <Form.Item style={{ textAlign: "right", marginTop: "24px" }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>Annuler</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={
                  createUserMutation.isPending || updateUserMutation.isPending
                }
              >
                {editingUser ? "Mettre à jour" : "Créer"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de détails utilisateur */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined />
            Détails de l'utilisateur
          </Space>
        }
        open={isDetailModalVisible}
        onCancel={() => {
          setIsDetailModalVisible(false);
          setViewingUser(null);
        }}
        footer={[
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setIsDetailModalVisible(false);
              if (viewingUser) handleEdit(viewingUser);
            }}
          >
            Modifier
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setIsDetailModalVisible(false);
              setViewingUser(null);
            }}
          >
            Fermer
          </Button>,
        ]}
        width={900}
      >
        {viewingUser && (
          <div>
            <Row gutter={[16, 16]}>
              {/* Informations principales */}
              <Col span={12}>
                <Card size="small" title="Informations personnelles">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Nom complet">
                      {viewingUser.first_name} {viewingUser.last_name}
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">
                      <Space>
                        <MailOutlined />
                        {viewingUser.email}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Téléphone">
                      <Space>
                        <PhoneOutlined />
                        {viewingUser.phone || "Non renseigné"}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Rôle">
                      <Tag
                        color={getRoleColor(viewingUser.role)}
                        icon={getRoleIcon(viewingUser.role)}
                      >
                        {getRoleText(viewingUser.role)}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Statut">
                      <Badge
                        status={viewingUser.is_active ? "success" : "error"}
                        text={viewingUser.is_active ? "Actif" : "Inactif"}
                      />
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              {/* Informations entreprise */}
              <Col span={12}>
                <Card size="small" title="Entreprise">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Nom de l'entreprise">
                      {viewingUser.tenant_name || "Aucune entreprise"}
                    </Descriptions.Item>
                    <Descriptions.Item label="ID Entreprise">
                      {viewingUser.tenant_id || "N/A"}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                <Card
                  size="small"
                  title="Vérification"
                  style={{ marginTop: 16 }}
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Email vérifié">
                      {viewingUser.email_verified ? (
                        <Tag color="success" icon={<CheckCircleOutlined />}>
                          Vérifié
                        </Tag>
                      ) : (
                        <Tag color="warning" icon={<CloseCircleOutlined />}>
                          Non vérifié
                        </Tag>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              {/* Activité */}
              <Col span={24}>
                <Card size="small" title="Activité">
                  <Descriptions column={2} size="small">
                    <Descriptions.Item label="Dernière connexion">
                      <Space>
                        <ClockCircleOutlined />
                        {formatDate(viewingUser.last_login_at)}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Compte créé le">
                      <Space>
                        <CalendarOutlined />
                        {formatDate(viewingUser.created_at)}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Dernière modification">
                      <Space>
                        <ClockCircleOutlined />
                        {formatDate(viewingUser.updated_at)}
                      </Space>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              {/* Permissions (si disponibles) */}
              {viewingUser.permissions &&
                Object.keys(viewingUser.permissions).length > 0 && (
                  <Col span={24}>
                    <Card size="small" title="Permissions">
                      <Space wrap>
                        {Object.entries(viewingUser.permissions).map(
                          ([key, value]) => (
                            <Tag
                              key={key}
                              color={value ? "green" : "red"}
                              icon={
                                value ? (
                                  <CheckCircleOutlined />
                                ) : (
                                  <CloseCircleOutlined />
                                )
                              }
                            >
                              {key}
                            </Tag>
                          )
                        )}
                      </Space>
                    </Card>
                  </Col>
                )}
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SuperAdminUsers;
